import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import inventoryApi from '../api/inventoryApi';
import { formatCurrency } from '../utils/currency';
import '../styles/InventoryDetail.css';

function InventoryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const handleBack = () => {
    if (item && item.inventoryType === 'ASSET') {
      navigate('/assets');
    } else {
      navigate('/materials');
    }
  };
  const [movements, setMovements] = useState([]);
  const [stockLocations, setStockLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchItemDetails();
    fetchItemHistory();
    fetchItemStockLocations();
  }, [id]);

  const fetchItemStockLocations = async () => {
    try {
      const response = await inventoryApi.getItemStockLocations(id);
      setStockLocations(response.data);
    } catch (err) {
      console.error('Failed to fetch item stock locations', err);
    }
  };

  const fetchItemDetails = async () => {
    try {
      setLoading(true);
      const response = await inventoryApi.getItem(id);
      setItem(response.data);
    } catch (err) {
      setError('Failed to fetch item details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchItemHistory = async () => {
    try {
      const response = await inventoryApi.getItemHistory(id);
      setMovements(response.data || []);
    } catch (err) {
      console.error('Failed to fetch item history', err);
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      AVAILABLE: 'status-available',
      IN_USE: 'status-in-use',
      MAINTENANCE: 'status-maintenance',
      DAMAGED: 'status-damaged',
      RETIRED: 'status-retired'
    };
    return statusColors[status] || 'status-default';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="id-loading">Loading...</div>;
  }

  if (error || !item) {
    return (
      <div className="id-error">
        <p>{error || 'Item not found'}</p>
        <button onClick={handleBack} className="id-btn-primary">
          Back to Inventory
        </button>
      </div>
    );
  }

  return (
    <div className="id-container">
      <div className="id-header">
        <button onClick={handleBack} className="id-btn-back">
          ← Back
        </button>
        <h1>Inventory Item Details</h1>
      </div>

      <div className="id-content">
        {/* Main Info Card */}
        <div className="id-card">
          <div className="id-card-header">
            <h2>{item.name}</h2>
            <span className={`id-badge ${getStatusBadge(item.status)}`}>
              {item.status}
            </span>
          </div>
          
          <div className="id-info-grid">
            <div className="id-info-item">
              <label>Item Code</label>
              <span>{item.itemCode}</span>
            </div>
            <div className="id-info-item">
              <label>Category</label>
              <span>{item.categoryName || 'N/A'}</span>
            </div>
            <div className="id-info-item">
              <label>Inventory Type</label>
              <span>{item.inventoryType === 'MATERIAL' ? 'Material' : 'Asset'}</span>
            </div>
            <div className="id-info-item">
              <label>Serial Number</label>
              <span>{item.serialNumber || 'N/A'}</span>
            </div>
            <div className="id-info-item">
              <label>Unit of Measurement</label>
              <span>{item.unitOfMeasurement}</span>
            </div>
            <div className="id-info-item">
              <label>Quantity</label>
              <span>{item.quantity}</span>
            </div>
            <div className="id-info-item">
              <label>Min Stock Level</label>
              <span>{item.minStockLevel || 'N/A'}</span>
            </div>
            <div className="id-info-item">
              <label>Purchase Price</label>
              <span>{formatCurrency(item.purchasePrice)}</span>
            </div>
            <div className="id-info-item">
              <label>Current Value</label>
              <span>{formatCurrency(item.currentValue)}</span>
            </div>
          </div>

          {item.description && (
            <div className="id-description">
              <label>Description</label>
              <p>{item.description}</p>
            </div>
          )}

          {item.notes && (
            <div className="id-description">
              <label>Notes</label>
              <p>{item.notes}</p>
            </div>
          )}
        </div>

        {item.inventoryType === 'MATERIAL' && (
          <div className="id-card">
            <h2>Stock Locations Breakdown</h2>
            <div style={{ marginBottom: '15px' }}>
              <span style={{ fontSize: '14px', color: '#666' }}>
                Total stock across all locations: <strong>{stockLocations.reduce((sum, loc) => sum + loc.quantity, 0)} {item.unitOfMeasurement}</strong>
              </span>
            </div>
            {stockLocations.length === 0 ? (
              <p className="id-empty">This item currently has no stock in any warehouse or project.</p>
            ) : (
              <div className="table-responsive" style={{ marginTop: '10px' }}>
                <table className="table" style={{ fontSize: '13px', width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
                      <th style={{ padding: '8px 12px' }}>Location Name</th>
                      <th style={{ padding: '8px 12px' }}>Location Type</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right' }}>Stock Level</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockLocations.map((loc, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #f9f9f9' }}>
                        <td style={{ padding: '8px 12px', fontWeight: 'bold' }}>
                          {loc.type === 'Warehouse' ? '🏢 ' : '🚧 '}
                          {loc.name}
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          <span className={`badge badge-${loc.type === 'Warehouse' ? 'info' : 'warning'}`}>
                            {loc.type}
                          </span>
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 'bold' }}>
                          {loc.quantity} {item.unitOfMeasurement}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {item.inventoryType !== 'MATERIAL' && (
          <>
            {/* Assignment Info Card */}
            <div className="id-card">
              <h2>Assignment Information</h2>
              <div className="id-info-grid">
                <div className="id-info-item">
                  <label>Current Location</label>
                  <span>{item.currentLocation || 'Not specified'}</span>
                </div>
                <div className="id-info-item">
                  <label>Assigned Project</label>
                  <span>{item.assignedProjectName || 'Not assigned'}</span>
                </div>
                <div className="id-info-item">
                  <label>Responsible Employee</label>
                  <span>{item.responsibleEmployeeName || 'Not assigned'}</span>
                </div>
                <div className="id-info-item">
                  <label>Branch</label>
                  <span>{item.branchName || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Movement History */}
            <div className="id-card">
              <h2>Movement History</h2>
              {movements.length === 0 ? (
                <p className="id-empty">No movement history available</p>
              ) : (
                <div className="id-timeline">
                  {movements.map((movement) => (
                    <div key={movement.id} className="id-timeline-item">
                      <div className="id-timeline-marker" />
                      <div className="id-timeline-content">
                        <div className="id-timeline-header">
                          <span className="id-movement-type">{movement.movementType}</span>
                          <span className="id-movement-date">{formatDate(movement.movementDate)}</span>
                        </div>
                        {movement.reason && (
                          <p className="id-movement-reason"><strong>Reason:</strong> {movement.reason}</p>
                        )}
                        {movement.notes && (
                          <p className="id-movement-notes">{movement.notes}</p>
                        )}
                        <div className="id-movement-details">
                          {movement.fromLocation && (
                            <span>From: {movement.fromLocation}</span>
                          )}
                          {movement.toLocation && (
                            <span>To: {movement.toLocation}</span>
                          )}
                          {movement.performedByName && (
                            <span>By: {movement.performedByName}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default InventoryDetail;
